/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { ApplicationRef, Injectable, Provider } from '@angular/core';
import { lastValueFrom, Observable, of as observableOf } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { ResponseCacheService } from './response-cache.service';

@Injectable({
    providedIn: 'root',
})
export class ApiHttpStateInterceptor implements HttpInterceptor {
    private isCacheActive = true;

    constructor(
        appRef: ApplicationRef,
        private readonly responseCache: ResponseCacheService
    ) {
        // Stop using the cache if the application has stabilized, indicating initial rendering is complete.
        lastValueFrom(
            appRef.isStable.pipe(
                filter((isStable: boolean) => isStable),
                take(1)
            )
        ).then(() => {
            this.isCacheActive = false;
        });
    }

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Stop using the cache if there is a mutating call.
        if (req.method !== 'GET' && req.method !== 'HEAD') {
            this.isCacheActive = false;
            this.responseCache.invalidate(req.url);
        }

        if (!this.isCacheActive || !this.responseCache.urlCacheRegexWhitelist.some((r) => r.test(req.url))) {
            // Cache is no longer active or is not whitelisted. Pass the request through.
            return next.handle(req);
        }

        const [memoryKey, storeKey] = this.responseCache.makeCacheKeys(req.method, req.urlWithParams);
        const response = this.responseCache.get(memoryKey, storeKey);

        if (response) {
            // Request found in cache. Respond using it.

            return observableOf(
                new HttpResponse<any>({
                    body: response.body,
                    headers: new HttpHeaders(response.headers),
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url,
                })
            );
        } else {
            // Request not found in cache. Make the request and cache it.
            const httpEvent$ = next.handle(req);

            return httpEvent$.pipe(
                tap((event: HttpEvent<unknown>) => {
                    if (event instanceof HttpResponse) {
                        this.responseCache.set(storeKey, memoryKey, event);
                    }
                })
            );
        }
    }
}

export const apiHttpStateInterceptorProvider: Provider = {
    provide: HTTP_INTERCEPTORS,
    useClass: ApiHttpStateInterceptor,
    multi: true,
};
